

import React, { useState, useEffect } from 'react';
import {  useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/AdminAttribute.css';

const AdminAttribute = () => {
  const navigate = useNavigate();
  
  // State for categories and selected category
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // State for attributes
  const [attributes, setAttributes] = useState([]);
  
  // State for form visibility
  const [showForm, setShowForm] = useState(false);
  
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    data_type: 'text',
    is_required: false
  });
  
  // State for editing mode
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // API URL
  const apiUrl = 'https://ecl.net.in/backende';

  // Fetch categories from API
  useEffect(() => {
    // Check if user is admin
    const isAdmin = sessionStorage.getItem('isAdmin');
    if (isAdmin !== 'true') {
      navigate('/login');
      return;
    }
    
    fetchCategories();
  }, [navigate]);

  // Fetch attributes when selected category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchAttributes(selectedCategory);
    }
  }, [selectedCategory]);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/equipment-categories`);
      
      // Ensure response data is an array
      const categoriesData = Array.isArray(response.data) ? response.data : [];
      setCategories(categoriesData);
      
      // Set default selected category if none is selected yet
      if (!selectedCategory && categoriesData.length > 0) {
        setSelectedCategory(categoriesData[0].id);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setErrorMessage('Failed to load categories. Please try again later.');
    }
  };

  // Fetch attributes for a specific category
  const fetchAttributes = async (categoryId) => {
    try {
      // First try to get attributes directly from the attributes endpoint
      const attrResponse = await axios.get(`${apiUrl}/api/equipment-attributes/category/${categoryId}`);
      
      if (Array.isArray(attrResponse.data)) {
        setAttributes(attrResponse.data);
        return;
      }
      
      // Fallback to getting them from the category endpoint if the direct endpoint doesn't work
      const response = await axios.get(`${apiUrl}/api/equipment-categories`);
      const categoryData = response.data.find(cat => cat.id === categoryId);
      
      if (categoryData && categoryData.attributes) {
        setAttributes(categoryData.attributes);
      } else {
        setAttributes([]);
      }
    } catch (error) {
      console.error('Error fetching attributes:', error);
      setErrorMessage('Failed to load attributes. Please try again later.');
      setAttributes([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle category selection change
  const handleCategoryChange = (e) => {
    setSelectedCategory(parseInt(e.target.value));
  };

  // Toggle form visibility
  const toggleForm = () => {
    setShowForm(!showForm);
    if (editMode || showForm) {
      resetForm();
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that display_name is properly set
    if (!formData.display_name.trim()) {
      setFormData({
        ...formData,
        display_name: formData.name // Use name as fallback if display_name is empty
      });
    }
    
    // Validate measurement name format
    const displayName = formData.display_name.trim();
    if (!displayName.includes('(') && ['capacity', 'weight', 'size'].some(term => 
        formData.name.toLowerCase().includes(term))) {
      // Suggest adding units for measurement attributes
      if (!window.confirm(`The display name "${displayName}" doesn't include units. For measurements, it's recommended to add units like (MT), (kg), etc. Continue anyway?`)) {
        return;
      }
    }
    
    try {
      if (editMode) {
        // Update existing attribute
        const response = await axios.put(
          `${apiUrl}/api/equipment-categories/${selectedCategory}/attributes/${editId}`,
          formData
        );
        
        if (response.data && response.data.success) {
          setSuccessMessage('Attribute updated successfully!');
          // Refresh attributes for the selected category
          fetchAttributes(selectedCategory);
        } else {
          setErrorMessage(response.data.message || 'Failed to update attribute');
          return;
        }
      } else {
        // Add new attribute
        const response = await axios.post(
          `${apiUrl}/api/equipment-categories/${selectedCategory}/attributes`,
          formData
        );
        
        if (response.data && response.data.success) {
          setSuccessMessage('Attribute added successfully!');
          // Refresh attributes for the selected category
          fetchAttributes(selectedCategory);
        } else {
          setErrorMessage(response.data.message || 'Failed to add attribute');
          return;
        }
      }
      
      // Reset form
      resetForm();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving attribute:', error);
      setErrorMessage('Failed to save attribute: ' + (error.response?.data?.message || error.message));
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setErrorMessage('');
      }, 3000);
    }
  };

  // Handle edit action
  const handleEdit = (attribute) => {
    setFormData({
      name: attribute.name || '',
      display_name: attribute.display_name || '',
      description: attribute.description || '',
      data_type: attribute.data_type || 'text',
      is_required: attribute.is_required || false
    });
    setEditMode(true);
    setEditId(attribute.id);
    setShowForm(true);
  };

  // Handle delete action
  const handleDelete = async (attributeId) => {
    if (!selectedCategory) {
      setErrorMessage('No category selected');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this attribute?')) {
      try {
        const response = await axios.delete(
          `${apiUrl}/api/equipment-categories/${selectedCategory}/attributes/${attributeId}`
        );
        
        if (response.data && response.data.success) {
          setSuccessMessage('Attribute deleted successfully!');
          // Refresh attributes for the selected category
          fetchAttributes(selectedCategory);
        } else {
          setErrorMessage(response.data.message || 'Failed to delete attribute');
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } catch (error) {
        console.error('Error deleting attribute:', error);
        setErrorMessage('Failed to delete attribute: ' + (error.response?.data?.message || error.message));
        
        // Clear error message after 3 seconds
        setTimeout(() => {
          setErrorMessage('');
        }, 3000);
      }
    }
  };

  // Reset form state
  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      data_type: 'text',
      is_required: false
    });
    setEditMode(false);
    setEditId(null);
    setShowForm(false);
  };

  // Filter attributes based on search term
  const filteredAttributes = attributes.filter(attribute => {
    if (!searchTerm) return true;
    
    // Safely handle potentially undefined properties
    const name = attribute.name || '';
    const displayName = attribute.display_name || '';
    const description = attribute.description || '';
    
    return name.toLowerCase().includes(searchTerm.toLowerCase()) || 
           displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           description.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get current category name
  const getCurrentCategoryName = () => {
    if (!selectedCategory) return '';
    const category = categories.find(cat => cat.id === selectedCategory);
    return category ? category.name : '';
  };

  // Get data type display name
  const getDataTypeDisplay = (dataType) => {
    switch(dataType) {
      case 'text': return 'Text';
      case 'number': return 'Number';
      case 'date': return 'Date';
      case 'boolean': return 'Yes/No';
      default: return dataType;
    }
  };

  // Helper function to check if a display name is properly formatted for measurements
  const isMeasurementAttribute = (name) => {
    return ['capacity', 'size', 'weight', 'dimension', 'measure'].some(term => 
      name.toLowerCase().includes(term)
    );
  };

  return (
    <div className="admin-attribute">
      {/* Success and Error Messages */}
      {successMessage && <div className="success-message">{successMessage}</div>}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      <div className="control-row">
        <div className="select-category">
          <label>Select Category:</label>
          <select value={selectedCategory || ''} onChange={handleCategoryChange}>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
        
        <button className="add-attribute-btn" onClick={toggleForm}>
          {showForm ? '- Cancel' : '+ Add New Attribute'}
        </button>
        
        <div className="search-box">
          <input
            type="text"
            placeholder="Search attributes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="attribute-form">
          <h2>{editMode ? 'Edit Attribute' : 'Add New Attribute'}</h2>
          
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Attribute Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Technical name (e.g., capacity)"
                  required
                />
                <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#666' }}>
                  The internal name used for storing data
                </small>
              </div>
              
              <div className="form-group">
                <label>Management Name *</label>
                <input
                  type="text"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleInputChange}
                  placeholder="User-friendly name (e.g., Capacity (MT))"
                  required
                />
                <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#666' }}>
                  The name shown to users in the equipment table
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter description (optional)"
                ></textarea>
              </div>
            </div>
            
            <div className="button-row">
              <button type="submit" className="save-button">
                {editMode ? 'Update Attribute' : 'Save Attribute'}
              </button>
              <button type="button" className="cancel-button" onClick={resetForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <h3 className="attributes-section-title">Attributes for: {getCurrentCategoryName()}</h3>

      <table className="attributes-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Management Name</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAttributes.length > 0 ? (
            filteredAttributes.map(attribute => (
              <tr key={attribute.id}>
                <td>{attribute.id}</td>
                <td>{attribute.name}</td>
                <td>
                  {attribute.display_name || '-'}
                  {isMeasurementAttribute(attribute.name) && 
                   (!attribute.display_name || !attribute.display_name.includes('(')) && (
                    <span style={{ color: 'orange', marginLeft: '5px', fontSize: '12px' }}>
                      ⚠️ Missing units
                    </span>
                  )}
                </td>
                <td>{attribute.description || '-'}</td>
                <td className="action-buttons">
                  <button className="edit-btn" onClick={() => handleEdit(attribute)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleDelete(attribute.id)}>Delete</button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="no-results">No attributes found for this category.</td>
            </tr>
          )}
        </tbody>
      </table>

      {filteredAttributes.length > 0 && (
        <div className="pagination">
          <button disabled>&lt; Previous</button>
          <div className="page-numbers">
            <button className="active">1</button>
          </div>
          <button disabled>Next &gt;</button>
        </div>
      )}

      {/* <div className="back-link">
        <Link to="/admin/master-equipment">← Back to Master Equipment</Link>
      </div> */}
    </div>
  );
};

export default AdminAttribute;